import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../reduxStore/Store';
import { importFamillesExcel } from '../../reduxStore/FamilleSlice';
import { GeneriqueDataType } from '../../constants/enums';
import api from '../../reduxStore/api';
import { useImportExcel } from '../../hooks/useImportExcel';
import ImportRejetsPanel, { ImporterValidesButton } from './ImportRejetsPanel';

const PREVIEW_COLUMNS = ['nom', 'description', 'visible', 'parent_nom'];

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  type: GeneriqueDataType;   // hiérarchie ciblée (FAMILLE_ARTICLE, CATEGORIE_CLIENT…)
  entityLabel: string;       // 'famille' / 'catégorie'
}

const ImportFamillesDialog: React.FC<Props> = ({ open, onClose, onImported, type, entityLabel }) => {
  const dispatch = useDispatch<AppDispatch>();

  // Une ligne « valide » dont le parent est une ligne en erreur du fichier sera refusée au
  // réimport des valides (parent introuvable) : elle rejoint alors les lignes en erreur.
  const imp = useImportExcel({
    envoyer: async (lignes) => {
      const action = await dispatch(importFamillesExcel({ lignes, type }));
      if (importFamillesExcel.fulfilled.match(action)) return action.payload;
      throw new Error(String(action.payload ?? 'Erreur inconnue'));
    },
    onImported,
    onClose,
  });
  const { rows, result } = imp;

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/familles/template', { params: { type }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `modele_${type.toLowerCase()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const previewCols = rows.length > 0
    ? PREVIEW_COLUMNS.filter(col => rows.some(r => r[col] != null && r[col] !== ''))
    : [];

  return (
    <Dialog open={open} onClose={imp.fermer} maxWidth="lg" fullWidth>
      <DialogTitle>Importer des {entityLabel}s depuis Excel</DialogTitle>

      <DialogContent dividers>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>
            Télécharger le modèle
          </Button>
          <Button variant="contained" size="small" startIcon={<UploadFileIcon />} onClick={imp.choisirFichier}>
            {imp.fileName ? 'Changer le fichier' : 'Choisir un fichier'}
          </Button>
          {imp.fileName && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {imp.fileName} — {rows.length} ligne{rows.length > 1 ? 's' : ''}
            </Typography>
          )}
          <input ref={imp.inputRef} type="file" accept=".xlsx,.xls" hidden onChange={imp.handleFileChange} />
        </Box>

        <Typography variant="body2" color="text.secondary" mb={2}>
          <code>parent_nom</code> vide = {entityLabel} racine. Le parent peut être une {entityLabel} existante
          ou une autre ligne du fichier, dans n'importe quel ordre.
        </Typography>

        {imp.parseError && <Alert severity="error" sx={{ mb: 2 }}>{imp.parseError}</Alert>}

        {result && result.errors.length === 0 && (
          <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mb: 2 }}>
            {result.success} {entityLabel}{result.success > 1 ? 's' : ''} importée{result.success > 1 ? 's' : ''} avec succès.
          </Alert>
        )}

        {result && result.errors.length > 0 && (
          <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} mb={0.5}>
              Aucune ligne importée — {result.errors.length} erreur{result.errors.length > 1 ? 's' : ''} détectée{result.errors.length > 1 ? 's' : ''} :
            </Typography>
            {result.errors.map((e, i) => (
              <Typography key={i} variant="body2">
                • {e.row > 0 ? `Ligne ${imp.ligneExcel(e.row)} : ` : ''}{e.message}
              </Typography>
            ))}
          </Alert>
        )}

        <ImportRejetsPanel
          rejetees={imp.rejetees.length}
          valides={imp.valides?.lignes.length ?? 0}
          validesImportees={imp.validesImportees}
          onTelechargerErreurs={imp.telechargerErreurs}
          onTelechargerValides={imp.telechargerValides}
        />

        {rows.length > 0 && result === null && (
          <>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', display: 'block', mb: 1 }}>
              Aperçu — {rows.length} ligne{rows.length > 1 ? 's' : ''} à importer
            </Typography>
            <Paper variant="outlined" sx={{ overflow: 'auto', maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', bgcolor: 'grey.100' }}>#</TableCell>
                    {previewCols.map(col => (
                      <TableCell key={col} sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', bgcolor: 'grey.100', whiteSpace: 'nowrap' }}>
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow key={idx} sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>{imp.lignesExcel[idx]}</TableCell>
                      {previewCols.map(col => (
                        <TableCell key={col} sx={{ fontSize: '0.8rem', fontFamily: typeof row[col] === 'number' ? 'monospace' : undefined }}>
                          {row[col] != null && row[col] !== '' ? String(row[col]) : <span style={{ color: '#bbb' }}>—</span>}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={imp.fermer} disabled={imp.importing}>Fermer</Button>
        {result === null && (
          <Button
            variant="contained"
            disabled={rows.length === 0 || imp.importing}
            onClick={imp.importer}
            startIcon={imp.importing ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {imp.importing ? 'Import en cours…' : `Importer ${rows.length > 0 ? rows.length + ' ligne' + (rows.length > 1 ? 's' : '') : ''}`}
          </Button>
        )}
        {result !== null && imp.valides && (
          <ImporterValidesButton valides={imp.valides.lignes.length} importing={imp.importing} onClick={imp.importerValides} />
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportFamillesDialog;
